import { Client, Account, Databases, ID, Query } from "appwrite";

// ==========================================
// APPWRITE CONFIGURATION
// ==========================================
// ⚠️ UPDATE THESE VALUES with your Appwrite project details
// Get these from your Appwrite Console (https://cloud.appwrite.io)
//
// 1. APPWRITE_PROJECT_ID: Found in Project Settings
// 2. DATABASE_ID: Found in Databases section after creating a database
// 3. COLLECTIONS: Use custom IDs when creating, or update with auto-generated IDs
// ==========================================

// Initialize Appwrite Client
const client = new Client()
  .setEndpoint("https://nyc.cloud.appwrite.io/v1")
  .setProject("694a25b30004a612d2ff");

// Export services
export const account = new Account(client);
export const databases = new Databases(client);

// ==========================================
// DATABASE CONFIGURATION
// ==========================================
// Update these IDs to match your Appwrite database and collections
// ==========================================

export const DATABASE_ID = "694a283400103b9cd6af"; // ⬅️ Your Database ID

// Collection IDs - must match exactly what's in your Appwrite Console
export const COLLECTIONS = {
  QUESTIONS: "questions", // ⬅️ Collection ID for questions (main question bank)
  SUBSCRIPTIONS: "subscriptions", // ⬅️ Collection ID for subscriptions
  QUESTION_ATTEMPTS: "question_attempts", // ⬅️ Collection ID for question_attempts
  USER_STATS: "user_stats", // ⬅️ Collection ID for user_stats
} as const;

// Debug: Log configuration
console.log("📦 Appwrite Config:", {
  endpoint: "https://nyc.cloud.appwrite.io/v1",
  projectId: "694a25b30004a612d2ff",
  databaseId: DATABASE_ID,
  collections: COLLECTIONS,
});

// Re-export utilities
export { ID, Query };
export { client };

/**
 * Test Appwrite connection - useful for debugging
 * Call this from browser console: window.testAppwriteConnection()
 */
export async function testAppwriteConnection(): Promise<{
  success: boolean;
  message: string;
  details?: any;
}> {
  const results: any = {
    endpoint: "https://nyc.cloud.appwrite.io/v1",
    projectId: "694a25b30004a612d2ff",
    databaseId: DATABASE_ID,
  };

  try {
    // Test 1: Check if we can reach the Appwrite server
    console.log("🔍 Testing Appwrite connection...");

    // Test 2: Try to get account (will fail if not logged in, but that's okay)
    try {
      const user = await account.get();
      results.auth = {
        status: "logged_in",
        userId: user.$id,
        email: user.email,
      };
    } catch (e: any) {
      results.auth = { status: "not_logged_in", message: "No active session" };
    }

    // Test 3: Try to list documents (will fail if collection doesn't exist)
    try {
      await databases.listDocuments(DATABASE_ID, COLLECTIONS.SUBSCRIPTIONS, []);
      results.collections = { subscriptions: "✅ exists" };
    } catch (e: any) {
      results.collections = {
        subscriptions:
          e.code === 404 ? "❌ not found" : `❌ error: ${e.message}`,
      };
    }

    console.log("📦 Appwrite Connection Test Results:", results);
    return {
      success: true,
      message: "Connection test complete",
      details: results,
    };
  } catch (error: any) {
    console.error("❌ Appwrite connection failed:", error);
    return {
      success: false,
      message: `Connection failed: ${error.message}`,
      details: { ...results, error: error.message },
    };
  }
}

// Make test function available globally for debugging in browser
try {
  (globalThis as any).testAppwriteConnection = testAppwriteConnection;
} catch {
  // Ignore if globalThis is not available
}
